import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { PageLoader, Spinner, Countdown } from "@/components/Kit";
import { playSfx, playJingle } from "@/lib/sound";
import { Landmark, Coins, ArrowDownToLine, ArrowUpFromLine, TrendingUp, Vault } from "lucide-react";

export default function Bank() {
  const { patchUser } = useOutletContext();
  const [d, setD] = useState(null);
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/bank").then((r) => setD(r.data));
  useEffect(() => { load(); }, []);
  if (!d) return <PageLoader label="Entering the vault" />;

  const amt = parseInt(amount, 10);
  const act = async (kind) => {
    if (!amt || amt <= 0) { toast.error("Enter a valid amount."); return; }
    setBusy(kind);
    try {
      const { data } = await api.post(`/bank/${kind}`, { amount: amt });
      setD((p) => ({ ...p, credits: data.credits, bank: data.bank, rate: p.rate }));
      patchUser({ credits: data.credits, bits: data.credits });
      setAmount(""); playSfx("coin");
      toast.success(kind === "deposit" ? `Deposited ${amt} Bits.` : `Withdrew ${amt} Bits.`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };
  const collect = async () => {
    setBusy("collect");
    try {
      const { data } = await api.post("/bank/collect", {});
      patchUser({ credits: data.credits, bits: data.credits }); playJingle();
      toast.success(`Interest collected: +${data.collected} Bits!`);
      await load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(null); }
  };

  return (
    <div data-testid="bank-page">
      <span className="label-cap text-primary flex items-center gap-2"><Landmark className="w-4 h-4" /> The Core Reserve // Vault Guard</span>
      <h1 className="text-4xl font-black tracking-tighter mt-2 mb-1 font-head">The Core Reserve</h1>
      <p className="text-muted-foreground mb-6">Bank your Bits to earn daily interest. Larger balances unlock higher APR tiers. Interest must be collected each day or it's forfeited.</p>

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <div className="panel p-5" data-testid="bank-onhand">
          <div className="label-cap flex items-center gap-1.5"><Coins className="w-3.5 h-3.5 text-secondary" /> On-Hand Bits</div>
          <div className="font-head font-black text-3xl mt-1 text-secondary mono">{d.credits}</div>
        </div>
        <div className="panel p-5" data-testid="bank-balance">
          <div className="label-cap flex items-center gap-1.5"><Vault className="w-3.5 h-3.5 text-primary" /> Vault Balance</div>
          <div className="font-head font-black text-3xl mt-1 text-primary mono">{d.bank}</div>
        </div>
        <div className="panel p-5" data-testid="bank-rate">
          <div className="label-cap flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-accent" /> {d.tier} · APR</div>
          <div className="font-head font-black text-3xl mt-1 text-accent mono">{(d.rate * 100).toFixed(1)}%</div>
        </div>
      </div>

      {d.tiers && (
        <div className="panel p-5 mb-6" data-testid="bank-tiers">
          <div className="label-cap mb-3">Core Reserve Tiers</div>
          <div className="space-y-2">
            {d.tiers.map((t) => {
              const active = t.name === d.tier;
              return (
                <div key={t.name} data-testid={`bank-tier-${t.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={`flex items-center justify-between px-3 py-2 rounded-[var(--radius)] border ${active ? "border-primary bg-primary/10" : "border-border"}`}>
                  <div>
                    <span className="font-head font-bold text-sm">{t.name}</span>
                    {active && <span className="ml-2 label-cap text-[9px] text-primary">Active</span>}
                    <div className="label-cap text-[9px]">{t.min.toLocaleString()}+ Bits</div>
                  </div>
                  <span className="font-head font-black text-lg mono text-accent">{(t.apr * 100).toFixed(1)}% APR</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="panel-glow p-5 hud-corner mb-6">
        <label className="label-cap block mb-2">Amount</label>
        <input data-testid="bank-amount-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0"
          className="w-full mb-4 px-4 py-3 rounded-[var(--radius)] bg-muted border border-border mono text-lg focus:outline-none focus:border-primary" />
        <div className="grid grid-cols-2 gap-3">
          <button onClick={() => act("deposit")} data-testid="bank-deposit-btn" disabled={busy} className="btn-primary py-3">
            {busy === "deposit" ? <Spinner className="w-4 h-4" /> : <><ArrowDownToLine className="w-4 h-4" /> Deposit</>}
          </button>
          <button onClick={() => act("withdraw")} data-testid="bank-withdraw-btn" disabled={busy} className="btn-secondary py-3">
            {busy === "withdraw" ? <Spinner className="w-4 h-4" /> : <><ArrowUpFromLine className="w-4 h-4" /> Withdraw</>}
          </button>
        </div>
      </div>

      <div className="panel p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="bank-interest">
        <div>
          <div className="font-head font-bold">Daily Interest</div>
          <p className="text-sm text-muted-foreground">{d.interest_available ? `You have ${d.interest_amount} Bits waiting.` : <>Next payout in <Countdown seconds={d.next_interest_in} onDone={load} testId="interest-timer" className="text-secondary" />.</>}</p>
        </div>
        <button onClick={collect} data-testid="bank-collect-btn" disabled={busy || !d.interest_available} className={d.interest_available ? "btn-primary" : "btn-ghost opacity-60"}>
          {busy === "collect" ? <Spinner className="w-4 h-4" /> : <><Coins className="w-4 h-4" /> Collect {d.interest_available ? `+${d.interest_amount}` : ""}</>}
        </button>
      </div>
    </div>
  );
}
